import React, { useEffect, useState } from "react";
import { TL } from "../i18n";
import client from "../lib/api";
import { Card, Badge, Button, Input } from "../components/ui";
import { money } from "../lib/holdingsMath";
import { ArrowLeftRight, HandCoins, AlertTriangle, RefreshCw, Activity as ActivityIcon, FileDown, FileText, Mail, Send } from "lucide-react";

const FILTERS = [
  { v: "all", l: "All" },
  { v: "transaction", l: "Transactions" },
  { v: "benefit", l: "Benefits" },
  { v: "alert", l: "Alerts" },
];

const ICON = { transaction: ArrowLeftRight, benefit: HandCoins, alert: AlertTriangle };
const COLOR = { transaction: "info", benefit: "success", alert: "danger" };

const fmtTs = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  return isNaN(d.getTime()) ? String(ts).slice(0, 10) : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};
const verbTitle = (s) => { const m = String(s || "").match(/^(.*?)(\s—\s.*)$/); return m ? `${TL(m[1])}${m[2]}` : TL(s); };

export default function Activity() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [frm, setFrm] = useState("");
  const [to, setTo] = useState("");
  const [summary, setSummary] = useState(null);
  const [cfg, setCfg] = useState({ statement_recipient: "", statement_enabled: false, statement_day: 1 });
  const [stMsg, setStMsg] = useState("");

  useEffect(() => {
    client.get("/settings").then((r) => setCfg({
      statement_recipient: r.data.statement_recipient || "",
      statement_enabled: !!r.data.statement_enabled,
      statement_day: r.data.statement_day || 1,
      last_statement_sent: r.data.last_statement_sent,
    })).catch(() => {});
  }, []);

  const saveCfg = async () => {
    setStMsg("");
    try {
      await client.put("/settings", {
        statement_recipient: cfg.statement_recipient,
        statement_enabled: cfg.statement_enabled,
        statement_day: Number(cfg.statement_day) || 1,
      });
      setStMsg("Schedule saved.");
    } catch (e) { setStMsg("Could not save schedule."); }
  };

  const sendNow = async () => {
    setStMsg("Sending…");
    try {
      const { data } = await client.post("/statement/send-now", { recipient: cfg.statement_recipient || undefined });
      const st = data.delivery?.status;
      setStMsg(st === "sent"
        ? `Statement emailed to ${data.recipient} for ${data.period.frm} → ${data.period.to}.`
        : `Statement queued for ${data.recipient} (${data.period.frm} → ${data.period.to}). Email is in fallback until SendGrid is configured.`);
    } catch (e) { setStMsg("Could not send statement. Set a recipient first."); }
  };

  const load = () => {
    setLoading(true);
    const params = {};
    if (frm) params.frm = frm;
    if (to) params.to = to;
    client.get("/activity", { params }).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
    client.get("/activity/summary", { params }).then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [frm, to]);

  const download = async (fmt) => {
    const params = {};
    if (frm) params.frm = frm;
    if (to) params.to = to;
    if (filter !== "all") params.kind = filter;
    const res = await client.get(`/activity/statement.${fmt}`, { params, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `activity_statement.${fmt}`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const shown = items.filter((i) => filter === "all" || i.kind === filter);

  return (
    <div className="space-y-6 fadeup">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-head text-2xl sm:text-3xl flex items-center gap-2"><ActivityIcon size={24} /> {TL("Activity & Notifications")}</h1>
        <div className="ms-auto flex gap-2">
          <Button variant="ghost" onClick={() => download("csv")} data-testid="download-csv"><FileDown size={16} /> {TL("CSV")}</Button>
          <Button variant="ghost" onClick={() => download("pdf")} data-testid="download-pdf"><FileText size={16} /> {TL("PDF statement")}</Button>
          <Button variant="ghost" onClick={load} data-testid="activity-refresh"><RefreshCw size={16} /> {TL("Refresh")}</Button>
        </div>
      </div>
      <p className="text-sm opacity-70 max-w-3xl">{TL("A single audit trail of money movements (holdings acquired/disposed, benefit payments & reimbursements) and every alert breach across the suite. Filter by date range and download a statement for your auditor or tax advisor.")}</p>

      <Card className="flex flex-wrap items-end gap-3">
        <div className="w-40"><Input label={TL("From date")} type="date" value={frm} onChange={(e) => setFrm(e.target.value)} data-testid="activity-from" /></div>
        <div className="w-40"><Input label={TL("To date")} type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="activity-to" /></div>
        {(frm || to) && <Button variant="subtle" onClick={() => { setFrm(""); setTo(""); }} data-testid="activity-clear-dates">{TL("Clear dates")}</Button>}
        <div className="ms-auto flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`filter-${f.v}`}
              className={`rounded-full border px-3 py-1 text-sm lift ${filter === f.v ? "bg-[var(--primary)] text-white border-[var(--primary)]" : "border-black/15 dark:border-white/15"}`}>
              {TL(f.l)}
            </button>
          ))}
        </div>
      </Card>

      <Card className="space-y-3" data-testid="statement-schedule">
        <div className="flex items-center gap-2 font-head text-sm uppercase tracking-wide opacity-70"><Mail size={16} /> {TL("Scheduled monthly statement")}</div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-56 flex-1"><Input label={TL("Recipient email")} type="email" value={cfg.statement_recipient} onChange={(e) => setCfg({ ...cfg, statement_recipient: e.target.value })} data-testid="statement-recipient" placeholder={TL("owner@familyoffice.com")} /></div>
          <label className="flex items-center gap-2 pb-2 text-sm">
            <input type="checkbox" checked={cfg.statement_enabled} onChange={(e) => setCfg({ ...cfg, statement_enabled: e.target.checked })} data-testid="statement-enabled" className="h-4 w-4" />
            {TL("Email automatically each month")}
          </label>
          <div className="w-28"><Input label={TL("Day of month")} type="number" value={cfg.statement_day} onChange={(e) => setCfg({ ...cfg, statement_day: e.target.value })} data-testid="statement-day" /></div>
          <Button variant="subtle" onClick={saveCfg} data-testid="statement-save">{TL("Save schedule")}</Button>
          <Button onClick={sendNow} data-testid="statement-send-now"><Send size={16} /> {TL("Send last month now")}</Button>
        </div>
        {stMsg && <div className="text-sm opacity-70" data-testid="statement-msg">{stMsg}</div>}
        {cfg.last_statement_sent && <div className="text-xs opacity-50">Last auto-sent: {cfg.last_statement_sent}</div>}
      </Card>

      {summary && summary.pools.length > 0 && (
        <div className="space-y-3" data-testid="activity-summary">
          <div className="grid gap-4 sm:grid-cols-3">
            <Card className="space-y-1"><div className="text-xs opacity-60">{TL("Inflows (period)")}</div><div className="font-head text-2xl text-[#24A148]">{money(summary.totals.inflow)}</div></Card>
            <Card className="space-y-1"><div className="text-xs opacity-60">{TL("Outflows (period)")}</div><div className="font-head text-2xl text-[#DA1E28]">{money(summary.totals.outflow)}</div></Card>
            <Card className="space-y-1"><div className="text-xs opacity-60">{TL("Net change (period)")}</div><div className={`font-head text-2xl ${summary.totals.net < 0 ? "text-[#DA1E28]" : "text-[#24A148]"}`}>{money(summary.totals.net)}</div></Card>
          </div>
          <Card className="overflow-x-auto p-0">
            <table className="w-full text-sm" data-testid="pool-summary-table">
              <thead><tr className="border-b border-black/10 dark:border-white/10">
                <th className="p-3 text-start font-medium opacity-70">{TL("Currency pool")}</th>
                <th className="p-3 text-end font-medium opacity-70">{TL("Opening")}</th>
                <th className="p-3 text-end font-medium opacity-70">{TL("Inflows")}</th>
                <th className="p-3 text-end font-medium opacity-70">{TL("Outflows")}</th>
                <th className="p-3 text-end font-medium opacity-70">{TL("Net")}</th>
                <th className="p-3 text-end font-medium opacity-70">{TL("Closing")}</th>
              </tr></thead>
              <tbody>
                {summary.pools.map((p, i) => (
                  <tr key={i} className="border-b border-black/5 dark:border-white/5">
                    <td className="p-3">{p.name || "—"}</td>
                    <td className="p-3 text-end">{p.opening == null ? "—" : money(p.opening)}</td>
                    <td className="p-3 text-end text-[#24A148]">{money(p.inflow)}</td>
                    <td className="p-3 text-end text-[#DA1E28]">{money(p.outflow)}</td>
                    <td className={`p-3 text-end font-medium ${p.net < 0 ? "text-[#DA1E28]" : ""}`}>{money(p.net)}</td>
                    <td className="p-3 text-end">{p.closing == null ? "—" : money(p.closing)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      <Card className="p-0">
        {loading && <div className="p-6 text-center opacity-50">{TL("Loading…")}</div>}
        {!loading && shown.length === 0 && <div className="p-6 text-center opacity-50" data-testid="activity-empty">{TL("No activity yet. Transactions, benefit payments and alert breaches will appear here.")}</div>}
        <ul className="divide-y divide-black/5 dark:divide-white/5">
          {shown.map((i, idx) => {
            const Icon = ICON[i.kind] || ActivityIcon;
            return (
              <li key={idx} className="flex items-center gap-4 p-4" data-testid={`activity-${i.kind}-${idx}`}>
                <span className={`grid h-9 w-9 place-items-center rounded-full ${i.kind === "alert" ? "bg-[#DA1E28]/10" : "bg-[var(--primary)]/10"}`}>
                  <Icon size={16} className={i.kind === "alert" ? "text-[#DA1E28]" : "text-[var(--primary)]"} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{verbTitle(i.title)}</span>
                    <Badge color={COLOR[i.kind] || "neutral"}>{TL(i.subtype || i.kind)}</Badge>
                  </div>
                  <div className="truncate text-sm opacity-60">{i.detail}</div>
                </div>
                <div className="text-end">
                  {i.amount != null && <div className="font-head">{money(i.amount)}</div>}
                  <div className="text-xs opacity-50">{fmtTs(i.ts)}</div>
                </div>
              </li>
            );
          })}
        </ul>
      </Card>
    </div>
  );
}
